import type { OrderSummary } from '../../../../infrastructure/OMSClient';

const PARXL_CSV_HEADERS = [
  'merchant_name',
  'merchant_batch_no',
  'merchant_order_no',
  'recipient_first_name',
  'recipient_last_name',
  'recipient_addressline1',
  'recipient_addressline2',
  'recipient_addressline3',
  'city_suburb',
  'state',
  'postcode',
  'urb_code',
  'country',
  'recipient_nationality',
  'recipient_document_type',
  'recipient_id',
  'phone_country_code',
  'phone',
  'email',
  'shipment_notification',
  'weight',
  'weight_unit',
  'length',
  'width',
  'height',
  'dimensions_unit',
  'merchant_declared_currency',
  'shipment_type',
  'insure',
  'incoterm',
  'battery_type',
  'contains_battery',
  'battery_pack',
  'contains_dg',
  'service_option',
  'item.0.category',
  'item.0.description',
  'item.0.quantity',
  'item.0.subtotal_declared_value',
  'item.0.hs_code',
  'item.0.subtotal_weight',
  'item.0.origin_country',
  'item.1.category',
  'item.1.description',
  'item.1.quantity',
  'item.1.subtotal_declared_value',
  'item.1.hs_code',
  'item.1.subtotal_weight',
  'item.1.origin_country',
  'item.2.category',
  'item.2.description',
  'item.2.quantity',
  'item.2.subtotal_declared_value',
  'item.2.hs_code',
  'item.2.subtotal_weight',
  'item.2.origin_country',
];

const FIXED_VALUES = {
  merchant_name: 'SkinSeoul',
  weight_unit: 'kg',
  dimensions_unit: 'cm',
  shipment_type: 'international',
  insure: 'N',
  incoterm: 'DDU',
  contains_battery: 'N',
  contains_dg: 'N',
  service_option: 'standard',
  hs_code: '3304.99',
  item_weight_kg: 0.2,
  length: '15',
  width: '10',
  height: '10',
};

function escapeCSVField(value: string | number | undefined | null): string {
  if (value === null || value === undefined) return '';
  const str = String(value);
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

function extractPhoneCountryCode(phone: string): { countryCode: string; phoneNumber: string } {
  const cleanPhone = phone?.replace(/\s+/g, '') || '';
  if (cleanPhone.startsWith('+44')) {
    return { countryCode: '+44', phoneNumber: cleanPhone.slice(3) };
  }
  if (cleanPhone.startsWith('+61')) {
    return { countryCode: '+61', phoneNumber: cleanPhone.slice(3) };
  }
  if (cleanPhone.startsWith('+')) {
    const match = cleanPhone.match(/^(\+\d{1,3})(.*)$/);
    if (match && match[1] && match[2]) {
      return { countryCode: match[1], phoneNumber: match[2] };
    }
  }
  return { countryCode: '', phoneNumber: cleanPhone };
}

function orderToParxlRow(order: OrderSummary): string[] {
  const shipping = order.shippingAddress;
  const customer = order.customer;
  const phone = shipping?.phone || customer?.phone || '';
  const { countryCode, phoneNumber } = extractPhoneCountryCode(phone);

  const lineItems = order.lineItems || [];
  const totalQuantity = lineItems.reduce((sum, item) => sum + (item.quantity || 0), 0);
  const totalWeight = (FIXED_VALUES.item_weight_kg * totalQuantity).toFixed(2);

  const row: Record<string, string> = {
    merchant_name: FIXED_VALUES.merchant_name,
    merchant_batch_no: '',
    merchant_order_no: order.orderId,
    recipient_first_name: shipping?.firstName || '',
    recipient_last_name: shipping?.lastName || '',
    recipient_addressline1: shipping?.address1 || '',
    recipient_addressline2: shipping?.address2 || '',
    recipient_addressline3: '',
    city_suburb: shipping?.city || '',
    state: shipping?.state || '',
    postcode: shipping?.postcode || '',
    urb_code: '',
    country: shipping?.country || order.consignee?.countryCode || '',
    recipient_nationality: '',
    recipient_document_type: '',
    recipient_id: '',
    phone_country_code: countryCode,
    phone: phoneNumber,
    email: customer?.email || '',
    shipment_notification: '',
    weight: totalWeight,
    weight_unit: FIXED_VALUES.weight_unit,
    length: FIXED_VALUES.length,
    width: FIXED_VALUES.width,
    height: FIXED_VALUES.height,
    dimensions_unit: FIXED_VALUES.dimensions_unit,
    merchant_declared_currency: order.currency || 'USD',
    shipment_type: FIXED_VALUES.shipment_type,
    insure: FIXED_VALUES.insure,
    incoterm: FIXED_VALUES.incoterm,
    battery_type: '',
    contains_battery: FIXED_VALUES.contains_battery,
    battery_pack: '',
    contains_dg: FIXED_VALUES.contains_dg,
    service_option: FIXED_VALUES.service_option,
  };

  for (let i = 0; i < 3; i++) {
    const item = lineItems[i];
    const prefix = `item.${i}`;
    if (item) {
      const itemWeight = (FIXED_VALUES.item_weight_kg * (item.quantity || 1)).toFixed(2);
      row[`${prefix}.category`] = '';
      row[`${prefix}.description`] = item.name || '';
      row[`${prefix}.quantity`] = String(item.quantity || 0);
      row[`${prefix}.subtotal_declared_value`] = String(item.price || 0);
      row[`${prefix}.hs_code`] = FIXED_VALUES.hs_code;
      row[`${prefix}.subtotal_weight`] = itemWeight;
      row[`${prefix}.origin_country`] = 'KR';
    } else {
      row[`${prefix}.category`] = '';
      row[`${prefix}.description`] = '';
      row[`${prefix}.quantity`] = '';
      row[`${prefix}.subtotal_declared_value`] = '';
      row[`${prefix}.hs_code`] = '';
      row[`${prefix}.subtotal_weight`] = '';
      row[`${prefix}.origin_country`] = '';
    }
  }

  return PARXL_CSV_HEADERS.map((header) => escapeCSVField(row[header]));
}

const TARGET_COUNTRY_CODES = ['GB', 'UK', 'UNITED KINGDOM'];

export function filterUKOrders(orders: OrderSummary[]): OrderSummary[] {
  return orders.filter((order) => {
    const countryCode = (order.consignee?.countryCode || order.shippingAddress?.country || '').toUpperCase();
    return TARGET_COUNTRY_CODES.includes(countryCode);
  });
}

export function sortOrdersForExport(orders: OrderSummary[]): OrderSummary[] {
  return [...orders].sort((a, b) => {
    const dateA = new Date(a.orderDateTime || a.orderDate).getTime();
    const dateB = new Date(b.orderDateTime || b.orderDate).getTime();
    if (dateA !== dateB) return dateA - dateB;

    const idA = parseInt(a.orderId) || 0;
    const idB = parseInt(b.orderId) || 0;
    return idA - idB;
  });
}

export function generateParxlCSV(orders: OrderSummary[]): string {
  const ukOrders = filterUKOrders(orders);
  const sortedOrders = sortOrdersForExport(ukOrders);

  const headerRow = PARXL_CSV_HEADERS.join(',');
  const dataRows = sortedOrders.map((order) => orderToParxlRow(order).join(','));

  return [headerRow, ...dataRows].join('\n');
}

export function downloadParxlCSV(orders: OrderSummary[], startDate: string, endDate: string): void {
  const csvContent = generateParxlCSV(orders);
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = `parxl_template_${startDate}_${endDate}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function getUKOrderCount(orders: OrderSummary[]): number {
  return filterUKOrders(orders).length;
}

const TRACKING_EXPORT_HEADERS = ['Order ID', 'Country Code', 'Tracking Number'];

function getTrackingNumber(order: OrderSummary): string {
  return (
    order.shippingData?.trackingNumber ||
    order.parxlData?.plsShipmentReference ||
    order.deleoData?.deleoTrackingNo ||
    ''
  );
}

function getCountryCode(order: OrderSummary): string {
  return order.consignee?.countryCode || order.shippingAddress?.country || '';
}

function orderToTrackingRow(order: OrderSummary): string[] {
  return [
    escapeCSVField(order.orderId),
    escapeCSVField(getCountryCode(order)),
    escapeCSVField(getTrackingNumber(order)),
  ];
}

export function generateTrackingExportCSV(orders: OrderSummary[]): string {
  const sortedOrders = sortOrdersForExport(orders);
  const headerRow = TRACKING_EXPORT_HEADERS.join(',');
  const dataRows = sortedOrders.map((order) => orderToTrackingRow(order).join(','));
  return [headerRow, ...dataRows].join('\n');
}

export function downloadTrackingExportCSV(orders: OrderSummary[], startDate: string, endDate: string): void {
  const csvContent = generateTrackingExportCSV(orders);
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = `order_tracking_export_${startDate}_${endDate}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

